import React, {
    createContext,
    Dispatch,
    PropsWithChildren,
    SetStateAction,
    useContext,
    useState,
} from 'react'
import {
    ApolloQueryResult,
    InternalRefetchQueriesInclude,
} from '@apollo/client'

import { useClinic } from '~contexts/clinic-context'
import {
    FilterOp,
    OrderByInput,
    QueryInput,
    QueryKpiGroupsQuery,
    KpiGroupFieldsFragment,
    useQueryKpiGroupsQuery,
    namedOperations,
} from '~graphql/generated/graphql'

import { useDebounce } from '~utils/hooks/use-debounce'
import { eqStringFilter, eqBoolFilter } from '~utils/query-helpers'
import { useBoolean } from '@chakra-ui/react'
import { useLocalStorage } from '~utils/hooks/use-local-storage'
import { LocalStorageKeys } from '~config/constants'

const KpiGroupContext = createContext<KpiGroupsProviderType>(
    {} as KpiGroupsProviderType,
)

export function KpiGroupsProvider({ children }: PropsWithChildren) {
    const { clinic, clinicGroup } = useClinic()
    const [searchString, setSearchString] = useState('')
    const [showArchived, setShowArchived] = useBoolean()
    const [usePerDay, setUsePerDay] = useLocalStorage(
        LocalStorageKeys.usePerDay,
        true,
    )
    const [defaultKpiGroup, setDefaultKpiGroup] =
        useState<KpiGroupFieldsFragment | null>(null)

    const debouncedSearchString = useDebounce(searchString, 300)

    const filters: QueryInput[] = [
        clinicGroup
            ? eqStringFilter('clinic_group', clinicGroup?.id)
            : eqStringFilter('clinic', clinic?.id),
        ...(!showArchived ? [eqBoolFilter('is_archived', false)] : []),
        {
            column: 'name',
            filterOp: FilterOp.Ilike,
            value: {
                string: `%${debouncedSearchString}%`,
            },
        },
    ]

    const orderBy: OrderByInput = {
        column: 'name',
        isAscending: true,
    }

    const { data, loading, error, refetch } = useQueryKpiGroupsQuery({
        variables: {
            where: filters,
            orderBy,
        },
        skip: !clinic && !clinicGroup,
        onCompleted: data => {
            if (data?.queryKpiGroups.length > 0) {
                setDefaultKpiGroup(data.queryKpiGroups[0])
            }
        },
    })

    const kpiGroups = data?.queryKpiGroups || []

    return (
        <KpiGroupContext.Provider
            value={{
                searchString,
                setSearchString,
                showArchived,
                toggleArchived: setShowArchived.toggle,
                usePerDay,
                setUsePerDay,
                kpiGroups,
                defaultKpiGroup,
                refetchKpiGroups: refetch,
                isLoading: loading,
                refetchQueries: [namedOperations.Query.queryKpiGroups],
            }}
        >
            {children}
        </KpiGroupContext.Provider>
    )
}

type KpiGroupsProviderType = {
    searchString: string
    setSearchString: Dispatch<SetStateAction<string>>
    showArchived: boolean
    toggleArchived: () => void
    kpiGroups: KpiGroupFieldsFragment[]
    defaultKpiGroup: KpiGroupFieldsFragment | null
    refetchKpiGroups: () => Promise<ApolloQueryResult<QueryKpiGroupsQuery>>
    refetchQueries: InternalRefetchQueriesInclude
    isLoading: boolean
    usePerDay: boolean
    setUsePerDay: (usePerDay: boolean) => void
}

export function useKpiGroups(): KpiGroupsProviderType {
    return useContext(KpiGroupContext)
}
